import { countries } from "~/utils/static";
import type { Ref, ToRefs } from "vue";
import type { MaybeRef } from "@vueuse/shared";
import type { UserProfile } from "~/src/api";
import { getBitAtPosition } from "~/src/api";

type ParsedProfileOutput = {
  id: string;
  about: string | null;
  pronouns: string | null;
  age: string | null;
  location: string | null;
  link: string | null;
  createdAt: string | null;
};

const DefaultReturn: ParsedProfileOutput = {
  id: "",
  about: null,
  pronouns: null,
  age: null,
  location: null,
  link: null,
  createdAt: null,
};

const pronounMap = {
  hehim: "He/Him",
  sheher: "She/Her",
  theythem: "They/Them",
  rathernotsay: "Rather not say",
  askme: "Ask me",
  any: "Any",
};

export function parseProfileForDisplay(
  x: UserProfile | null | undefined,
  res?: "small" | "med" | "large" | "original",
  showPrivateIfAvailable?: boolean,
): ParsedProfileOutput;
export function parseProfileForDisplay(
  x: Ref<UserProfile | null | undefined>,
  res?: "small" | "med" | "large" | "original",
  showPrivateIfAvailable?: boolean,
): ToRefs<ParsedProfileOutput>;
export function parseProfileForDisplay(
  profileInput: MaybeRef<UserProfile | null | undefined>,
  res: "small" | "med" | "large" | "original" = "original",
  showPrivateIfAvailable = false,
): ParsedProfileOutput | ToRefs<ParsedProfileOutput> {
  let thumbRes: "640" | "1280" | "1920" | "original";
  switch (res) {
    case "small":
      thumbRes = "640";
      break;
    case "med":
      thumbRes = "1280";
      break;
    case "large":
      thumbRes = "1920";
      break;
    default:
      thumbRes = "original";
  }

  let id: MaybeRef<string>;
  let about: MaybeRef<string>;
  // TODO: properly type these
  let pronouns: MaybeRef<string | null>;
  let age: MaybeRef<string | null>;
  let location: MaybeRef<string | null>;
  let link: MaybeRef<string | null>;
  let createdAt: MaybeRef<string | null>;

  const getPrivacyBit = (pos: number) =>
    showPrivateIfAvailable ||
    !getBitAtPosition(unref(profileInput)?.attributes.privacy ?? 0, pos);

  if (isRef(profileInput)) {
    id = computed(() => profileInput.value?.id ?? "");
    about = computed(() => profileInput.value?.attributes?.about ?? "");
    pronouns = computed(() => {
      if (!getPrivacyBit(0)) return null;

      const pronoun: keyof typeof pronounMap | null =
        profileInput.value?.attributes?.pronouns ?? null;
      return pronoun ? pronounMap[pronoun] : null;
    });
    age = computed(() => {
      if (!getPrivacyBit(1)) return null;

      return profileInput.value?.attributes?.ageRange
        ? profileInput.value?.attributes?.ageRange === "55plus"
          ? "55+"
          : profileInput.value?.attributes?.ageRange
        : null;
    });
    location = computed(() => {
      if (!getPrivacyBit(2)) return null;

      return profileInput.value?.attributes?.location
        ? countries[
            profileInput.value?.attributes?.location as keyof typeof countries
          ]
        : null;
    });
    link = computed(() => profileInput.value?.attributes.link ?? null);
    createdAt = computed(() =>
      profileInput.value
        ? formatDateAlt(profileInput.value.attributes.createdAt)
        : null,
    );
  } else {
    if (!profileInput) return DefaultReturn;
    id = profileInput.id;
    about = profileInput?.attributes?.about ?? "";
    const pronoun: keyof typeof pronounMap | null =
      profileInput?.attributes?.pronouns ?? null;
    pronouns = pronoun && getPrivacyBit(0) ? pronounMap[pronoun] : null;
    age = (getPrivacyBit(1) && profileInput?.attributes?.ageRange) || null;
    location =
      getPrivacyBit(2) && profileInput?.attributes?.location
        ? countries[
            profileInput?.attributes?.location as keyof typeof countries
          ]
        : null;

    link = profileInput?.attributes.link ?? null;
    createdAt = profileInput?.attributes.createdAt ?? null;
  }

  return {
    id,
    about,
    pronouns,
    age,
    location,
    link,
    createdAt,
  } as ToRefs<ParsedProfileOutput> | ParsedProfileOutput;
}
